import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Download, RefreshCw } from 'lucide-react'
import { api, apiErrorMessage } from '../../lib/api'
import { downloadFile } from '../../lib/download'
import { useSyncJob } from '../../lib/useSyncJob'
import { ClientSelect } from '../../components/ClientSelect'
import { useClientSelection } from '../../stores/clientSelection'
import { ListFilters, EMPTY_FILTERS, type FilterState } from '../../components/ListFilters'
import { Pagination } from '../../components/Pagination'
import { fmtMoney, fmtDate } from '../../lib/format'

interface Row {
  id: number
  number: string
  category: string
  categoryName: string
  date: string
  totalWithVat: string
  isStorno: boolean
  country: string
  currency: string
  issuedBySeller: boolean
}

const TARI = ['RO', 'BG', 'HU'] as const
const STEAG: Record<string, string> = { RO: '🇷🇴', BG: '🇧🇬', HU: '🇭🇺' }
interface ListResp {
  items: Row[]
  total: number
  page: number
  pageSize: number
}

export function FacturiCumpararePage() {
  const clientId = useClientSelection((s) => s.selectedClientId)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS)
  /** '' = toate țările. Generatorul de note e însă per țară, deci exportul cere una anume. */
  const [country, setCountry] = useState<string>('')

  const { data, isLoading } = useQuery({
    queryKey: ['emag-supplier-invoices', clientId, page, pageSize, filters, country],
    queryFn: async () =>
      (await api.get<ListResp>('/emag/supplier-invoices', { params: {
          clientId,
          page,
          pageSize,
          ...(country ? { country } : {}),
          ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
          ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
          ...(filters.q.trim() ? { q: filters.q.trim() } : {}),
        } })).data,
    enabled: clientId != null,
  })

  const sync = useSyncJob('invoices', clientId, {
    successText: (r) => `Sincronizate ${r.supplier ?? 0} facturi cumpărare, ${r.customer ?? 0} vânzare`,
    invalidate: ['emag-supplier-invoices', 'emag-customer-invoices'],
  })

  const [exporting, setExporting] = useState(false)

  /**
   * Fisierul de import EA. Conturile, furnizorul si tratamentul TVA difera pe tari
   * (RO: 628 = 401 si 4426 = 401 cu taxid 'c'; BG/HU: taxare inversa, 4426 = 4427 cu
   * taxid 'c2.1'), deci exportul se face pe O SINGURA tara, nu pe toate deodata.
   */
  async function exportaImport() {
    if (clientId == null) return
    setExporting(true)
    try {
      // Exportul respecta intervalul din filtre, altfel fisierul aduce tot istoricul
      // desi pe ecran e o singura luna (Alexandru, 25.09.2026). Perioada intra si in
      // numele fisierului, ca sa nu se amestece lunile in folderul clientului.
      const p = new URLSearchParams({ clientId: String(clientId), country })
      if (filters.dateFrom) p.set('dateFrom', filters.dateFrom)
      if (filters.dateTo) p.set('dateTo', filters.dateTo)
      const sufix = filters.dateFrom || filters.dateTo
        ? `_${filters.dateFrom || 'inceput'}_${filters.dateTo || 'azi'}`
        : ''
      await downloadFile(
        `/ea-export/supplier-invoices/download?${p.toString()}`,
        `IMPORT_facturi_servicii_${country}${sufix}.xlsx`,
      )
      toast.success('Fișier descărcat')
    } catch (err) {
      toast.error(apiErrorMessage(err), { duration: 12000 })
    } finally {
      setExporting(false)
    }
  }

  const items = data?.items ?? []
  const total = data?.total ?? 0

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold text-b4l-text">Facturi cumpărare (servicii eMAG)</h1>
        <div className="flex items-center gap-2">
          <ClientSelect />
          <select
            value={country}
            onChange={(e) => { setCountry(e.target.value); setPage(1) }}
            className="rounded-md border border-b4l-border px-2.5 py-1.5 text-[13px] outline-none focus:border-b4l-green"
          >
            <option value="">Toate țările</option>
            {TARI.map((t) => (
              <option key={t} value={t}>{STEAG[t]} {t}</option>
            ))}
          </select>
          <button
            onClick={() => void exportaImport()}
            disabled={clientId == null || exporting || total === 0 || !country}
            title={
              country
                ? `Note de înregistrare pentru ${country}, pentru import prin Copy/Paste în ExpertAccounts`
                : 'Alege întâi o țară: conturile și tratamentul TVA diferă între RO și BG/HU'
            }
            className="flex items-center gap-1.5 rounded-md border border-b4l-border px-3 py-1.5 text-[13px] font-medium text-b4l-text hover:bg-b4l-bg-soft disabled:opacity-60"
          >
            <Download size={14} />
            {exporting ? 'Se generează…' : 'Export note contabile'}
          </button>
          <button
            onClick={() => void sync.start()}
            disabled={clientId == null || sync.running}
            className="flex items-center gap-1.5 rounded-md bg-b4l-green px-3 py-1.5 text-[13px] font-medium text-white hover:bg-b4l-green-dark disabled:opacity-60"
          >
            <RefreshCw size={14} className={sync.running ? 'animate-spin' : ''} />
            {sync.running ? 'Sincronizare în curs…' : 'Sincronizează'}
          </button>
        </div>
      </div>

      <div className="mb-3">
        <ListFilters value={filters} onChange={(v) => { setFilters(v); setPage(1) }} searchPlaceholder="număr, categorie…" />
      </div>

      {/* fara overflow-hidden: ar anula pozitionarea lipicioasa a antetului */}
      <div className="rounded-xl border border-b4l-border bg-white">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="sticky top-0 z-10 border-b border-b4l-border bg-white text-left text-b4l-text-muted shadow-[0_1px_0_0_rgb(229,231,235)]">
              <th className="px-4 py-2.5 font-medium">Țara</th>
              <th className="px-4 py-2.5 font-medium">Număr</th>
              <th className="px-4 py-2.5 font-medium">Categorie</th>
              <th className="px-4 py-2.5 font-medium">Data</th>
              <th className="px-4 py-2.5 text-right font-medium">Total</th>
            </tr>
          </thead>
          <tbody>
            {(isLoading || clientId == null) && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-b4l-text-dim">
                  {clientId == null ? 'Alege un client.' : 'Se încarcă…'}
                </td>
              </tr>
            )}
            {!isLoading && clientId != null && items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-b4l-text-dim">
                  Nicio factură. Apasă „Sincronizează".
                </td>
              </tr>
            )}
            {items.map((f) => (
              <tr key={f.id} className="border-b border-b4l-border last:border-0">
                <td className="px-4 py-2.5 whitespace-nowrap">
                  {STEAG[f.country] ?? ''} {f.country}
                </td>
                <td className="px-4 py-2.5 font-medium">
                  {f.number}
                  {f.isStorno && <span className="ml-1 text-b4l-red">(storno)</span>}
                  {f.issuedBySeller && (
                    <span
                      title="Emisă de noi către eMAG, în numele nostru — este factură de VÂNZARE, nu cheltuială. Nu intră în notele de înregistrare 628 = 401."
                      className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
                    >
                      emisă de noi
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5">{f.categoryName || f.category}</td>
                <td className="px-4 py-2.5">{fmtDate(f.date)}</td>
                <td className="px-4 py-2.5 text-right">{fmtMoney(f.totalWithVat, f.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPage={setPage}
          onPageSize={setPageSize}
          unit="facturi"
        />
      </div>

    </div>
  )
}
